import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import CheckoutSteps from "../components/CheckoutSteps";

function formatINR(n) {
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export function CartPage() {
  const navigate = useNavigate();
  const { items, removeFromCart, updateQty, cartTotal, startCheckoutWithCart, startCheckoutWithSingle } = useCart();

  const shipping = 0;
  const grandTotal = cartTotal + shipping;

  function handleBuyNow(item) {
    startCheckoutWithSingle(item, item.qty);
    navigate("/checkout");
  }

  function handleCheckoutAll() {
    startCheckoutWithCart();
    navigate("/checkout");
  }

  return (
    <section className="pt-32 pb-24 px-6 md:px-10 min-h-screen" style={{ background: "#0B0D10" }}>
      <div className="max-w-6xl mx-auto">
        <CheckoutSteps current="cart" />

        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="uppercase tracking-widest text-xs mb-2" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#FF8A00" }}>
              {items.length} {items.length === 1 ? "item" : "items"}
            </p>
            <h1 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-4xl md:text-5xl">
              Your Bag
            </h1>
          </div>
          <Link
            to="/"
            className="hidden sm:inline-flex items-center gap-2 text-sm uppercase tracking-widest"
            style={{ color: "rgba(245,241,234,0.6)" }}
          >
            <ArrowLeft size={15} /> Continue Shopping
          </Link>
        </div>

        {items.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-center py-24 rounded-2xl"
            style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
              style={{ background: "rgba(30,154,255,0.12)" }}
            >
              <ShoppingBag size={26} color="#1E9AFF" />
            </div>
            <h2 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-2xl mb-2">
              Your bag is empty
            </h2>
            <p className="text-sm mb-8" style={{ color: "rgba(245,241,234,0.55)" }}>
              Looks like you haven't added anything yet. Let's fix that.
            </p>
            <Link
              to="/"
              className="btn-primary px-7 py-3 rounded-full text-sm tracking-widest uppercase inline-flex items-center gap-2"
            >
              Start Shopping <ArrowRight size={15} />
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 flex flex-col gap-4">
              {items.map((item) => (
                <div
                  key={item.name}
                  className="flex flex-col sm:flex-row gap-5 p-5 rounded-2xl"
                  style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div className="w-full sm:w-28 h-40 sm:h-28 rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      loading="lazy"
                      className="w-full h-full"
                      style={{ objectFit: "cover" }}
                    />
                  </div>

                  <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p
                          className="text-xs uppercase tracking-widest mb-1"
                          style={{ fontFamily: "'JetBrains Mono', monospace", color: "rgba(245,241,234,0.5)" }}
                        >
                          {item.brand}
                        </p>
                        <h3 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-lg truncate">
                          {item.name}
                        </h3>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.name)}
                        aria-label={`Remove ${item.name}`}
                        className="flex-shrink-0 p-2 rounded-full transition-colors"
                        style={{ color: "rgba(245,241,234,0.5)" }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex items-center rounded-full overflow-hidden"
                          style={{ border: "1px solid rgba(255,255,255,0.12)" }}
                        >
                          <button
                            onClick={() => updateQty(item.name, item.qty - 1)}
                            className="w-8 h-8 flex items-center justify-center"
                            style={{ color: "#F5F1EA" }}
                            aria-label="Decrease quantity"
                          >
                            <Minus size={13} />
                          </button>
                          <span
                            className="w-8 text-center text-sm"
                            style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F1EA" }}
                          >
                            {item.qty}
                          </span>
                          <button
                            onClick={() => updateQty(item.name, item.qty + 1)}
                            className="w-8 h-8 flex items-center justify-center"
                            style={{ color: "#F5F1EA" }}
                            aria-label="Increase quantity"
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                        <span
                          style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F1EA" }}
                          className="text-sm"
                        >
                          ₹{formatINR(parseFloat(String(item.price).replace(/,/g, "")) * item.qty)}
                        </span>
                      </div>

                      <button
                        onClick={() => handleBuyNow(item)}
                        className="text-xs uppercase tracking-widest px-4 py-2 rounded-full"
                        style={{ background: "#FF8A00", color: "#fff" }}
                      >
                        Buy Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="rounded-2xl p-6 sticky top-28"
              style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <h2 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-xl mb-6">
                Order Summary
              </h2>
              <div className="flex flex-col gap-3 text-sm mb-6" style={{ color: "rgba(245,241,234,0.7)" }}>
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>₹{formatINR(cartTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#3FA9FF" }}>Free</span>
                </div>
              </div>
              <div
                className="flex justify-between items-center pt-4 mb-6"
                style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}
              >
                <span style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-lg">
                  Total
                </span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F1EA" }} className="text-lg">
                  ₹{formatINR(grandTotal)}
                </span>
              </div>
              <button
                onClick={handleCheckoutAll}
                className="btn-primary w-full py-3.5 rounded-full text-sm tracking-widest uppercase flex items-center justify-center gap-2"
              >
                Proceed to Checkout <ArrowRight size={15} />
              </button>
              <p className="text-xs text-center mt-4" style={{ color: "rgba(245,241,234,0.4)" }}>
                Free express shipping · 30-day easy returns
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default CartPage;
